/**
 * Group hub Home tab — overview, activity, actions due, and live group status.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ArrowRightIcon,
  ArrowSquareOutIcon,
  ArrowsClockwiseIcon,
  CalendarBlankIcon,
  ChatIcon,
  ClockIcon,
  DotIcon,
  FileIcon,
  GraduationCapIcon,
  SparkleIcon,
  UserPlusIcon
} from '@phosphor-icons/react';
import Button from '@/components/ui/Button';
import { CountBadge } from '@/components/ui/CountBadge';
import Modal from '@/components/ui/Modal';
import Loading from '@/components/ui/Loading';
import Skeleton from '@/components/ui/Skeleton';
import TitledPanel from '@/components/ui/TitledPanel';
import Tooltip from '@/components/ui/Tooltip';
import Pill from '@/components/ui/Pill';
import SegmentedControl from '@/components/ui/SegmentedControl';
import Dropdown from '@/components/ui/Dropdown';
import { useLocation } from 'react-router-dom';
import { resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import { listRecordAlerts } from 'src/features/training-hub/appointmentAlerts';
import { getEfmDomainLabel } from 'src/features/domains/efmDomains';
import type { AppointmentResponse } from 'src/features/training-hub/types';
import type { Meeting } from 'src/features/meetings/types';
import type {
  ActionExtractionResponse,
  ActivityItemFacts,
  ActivityItemKind,
  RecentActivityItem,
  StatutoryDocumentRow
} from '../api';
import { statutoryDocumentExpiryFeedItems, StatutoryDocumentIcon } from './StatutoryComplianceSummary';
import { formatActivityCopy } from '../formatRecentActivity';
import { useGroupOverview } from '../hooks';
import GroupsOverviewMarkdown from './GroupsOverviewMarkdown';
import GroupsOverviewAskInput, {
  GROUP_OVERVIEW_QUESTION_PREFIX,
  OVERVIEW_FOLLOW_UP_BUTTON_CLASS
} from './GroupsOverviewAskInput';
import type { Action, JoinRequest } from '../types';
import ActionsDataTable from './ActionsDataTable';
import HelpLinkList from 'src/features/help/HelpLinkList';
import { groupHubHelpLinks } from 'src/features/help/links';
import { formatLongDate, formatShortDate } from 'src/lib/utils/formatDate';

const ACTIVITY_PREVIEW_COUNT = 6;
const ACTIONS_PREVIEW_COUNT = 5;
const MEETINGS_PREVIEW_COUNT = 3;

const CHAIR_REPORT_CHAT_QUERY =
  "Write me a Chair's Report for this group, for the period covered by the most recent group meeting. If multiple meetings could be relevant, confirm with me before proceeding.";
const OPERATIONAL_REPORT_CHAT_QUERY = 'Write me an Operational Report for this group.';

const PFI_REPORTS = [
  {
    label: "Review provider's monthly report",
    query:
      "Draft my monthly review of the provider's report for this PFI contract, inside the 7-day approval window: an approve-or-challenge response covering performance against the obligations register, service failures and disputed items, queries, and points for the performance meeting. Do not draft it in this reply. The provider's monthly report pack is not attached — ask me to attach it, and wait. A meeting transcript, minutes, obligations, actions, or other group material is not that report, so do not use any of those as a substitute. After I attach the pack, draft the review from the pack together with this group's obligations, actions, and recent activity."
  },
  {
    label: 'Prepare performance meeting pack',
    query:
      "Prepare this month's performance meeting pack for this PFI group. Include the agenda, open actions by owner (Trust, SPV, and FM provider), what changed since last month, obligations that are due or breached, and items to escalate. Use this group's obligations, actions, and meetings."
  },
  {
    label: 'Draft progress update',
    query:
      "Draft a short progress update for the Director or SRO on this PFI contract. Cover status by workstream, what moved, risks, and decisions needed. Use this group's obligations, actions, and recent activity."
  }
] as const;
/** Home preview: only kinds we already have events for (see product table A). */
export const HOME_TIMELINE_KINDS = new Set<ActivityItemKind>([
  'extraction_report',
  'extraction_meeting',
  'meeting_finalised',
  'appointment_created',
  'appointment_revoked',
  'member_added',
  'group_created',
  'meeting_series_added',
  'meeting_added',
  'action_created',
  'action_state_changed',
  'action_progress_update',
  'dashboard_data_entered'
]);

type ActivityView = 'timeline' | 'requires_action';
export type HomeFeedKind =
  | ActivityItemKind
  | 'join_requested'
  | 'appointment_expiring'
  | 'statutory_document_expired'
  | 'statutory_document_expiring'
  | 'meeting_needs_finalising';
type ActivityTypeFilter = 'all' | 'actions' | 'meetings' | 'training' | 'group' | 'dashboards';

const ACTIVITY_TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'actions', label: 'Actions' },
  { value: 'meetings', label: 'Meetings' },
  { value: 'training', label: 'Training' },
  { value: 'group', label: 'Group' },
  { value: 'dashboards', label: 'Dashboards' }
] as const;

const ACTIVITY_TYPE_KINDS: Record<Exclude<ActivityTypeFilter, 'all'>, Set<HomeFeedKind>> = {
  actions: new Set([
    'action_created',
    'action_state_changed',
    'action_progress_update',
    'extraction_meeting',
    'extraction_report'
  ]),
  meetings: new Set([
    'meeting_added',
    'meeting_series_added',
    'meeting_resource_added',
    'meeting_finalised',
    'meeting_approval',
    'meeting_needs_finalising'
  ]),
  training: new Set(['appointment_created', 'appointment_revoked', 'certificate_added']),
  group: new Set(['member_added', 'group_created', 'join_requested', 'resource_shared']),
  dashboards: new Set(['dashboard_data_entered'])
};

export interface HomeFeedItem {
  kind: HomeFeedKind;
  title: string;
  subtitleParts: string[];
  timestamp: string | null;
  relevant_to_user: boolean;
  target_ids: string[];
  facts?: ActivityItemFacts;
  joinRequest?: JoinRequest;
  appointment?: AppointmentResponse;
  meeting?: Meeting;
  groupId?: string;
  groupName?: string;
}

export type UnfinalisedMeeting = Pick<Meeting, 'id' | 'title' | 'scheduled_date'>;

export function unfinalisedMeetingToFeedItem(meeting: UnfinalisedMeeting): HomeFeedItem {
  return {
    kind: 'meeting_needs_finalising',
    title: `Finalise the minutes for ${meeting.title || 'this meeting'}`,
    subtitleParts: [`Held on ${formatLongDate(meeting.scheduled_date)}`],
    timestamp: null,
    relevant_to_user: true,
    target_ids: [meeting.id]
  };
}

interface RecentActivityDayGroup {
  key: string;
  label: string;
  items: HomeFeedItem[];
}

export interface GroupHomeProgressCard {
  percent: number;
  detail: string;
  yesCount?: number;
  noCount?: number;
  evidencedCount?: number;
  expiringCount?: number;
  missingCount?: number;
  overdueCount?: number;
  barSegments?: { label: string; value: number; color: string }[];
}

interface GroupHomeTabProps {
  setupCard?: ReactNode;
  upcomingMeetings: Meeting[];
  actions: Action[];
  currentUserId?: number | null;
  recentActivity: RecentActivityItem[];
  isLoadingActivity: boolean;
  onActivityItemClick: (item: RecentActivityItem) => void;
  getActivityIcon: (item: RecentActivityItem) => ReactNode;
  onEditAction: (action: Action) => void;
  onViewAllActions: () => void;
  onViewAllMeetings: () => void;
  onOpenMeeting: (meetingId: string) => void;
  meetingsNeedingFinalising?: Meeting[];
  onCreateMeetingSeries?: () => void;
  training: GroupHomeProgressCard | null;
  onOpenTraining?: () => void;
  pam: GroupHomeProgressCard | null;
  onOpenPam?: () => void;
  statutoryCompliance: GroupHomeProgressCard | null;
  onOpenStatutoryCompliance?: () => void;
  onOpenMembers?: () => void;
  groupId?: string;
  groupName?: string;
  pendingJoinRequests?: JoinRequest[];
  trainingAppointments?: AppointmentResponse[];
  onOpenTrainingAppointment?: (appointment: AppointmentResponse) => void;
  statutoryDocuments?: StatutoryDocumentRow[];
  extractions?: ActionExtractionResponse[];
  onJoinRequestDecided?: () => void;
  /** Join requests, training expiry, etc. Group admins only. */
  canViewRequiresAction?: boolean;
  /** PFI groups draft the contract packs. Other groups keep the safety reports. */
  reportSet?: 'default' | 'pfi';
}

const parseActivityDate = (value: string): Date => new Date(value.includes('Z') ? value : `${value}Z`);

const activityDayInfo = (value: string | null, now: Date): { key: string; label: string } => {
  if (!value) return { key: 'unknown', label: 'Unknown date' };
  const date = parseActivityDate(value);
  if (Number.isNaN(date.getTime())) return { key: 'unknown', label: 'Unknown date' };

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (day.getTime() === today.getTime()) return { key: 'today', label: 'Today' };
  if (day.getTime() === yesterday.getTime()) return { key: 'yesterday', label: 'Yesterday' };
  return {
    key: `${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`,
    label: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  };
};

const formatActivityTime = (value: string | null) => {
  if (!value) return '';
  try {
    const date = parseActivityDate(String(value));
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return String(value);
  }
};

function requesterName(request: JoinRequest): string {
  if (request.requester_firstname && request.requester_surname) {
    return `${request.requester_firstname} ${request.requester_surname}`;
  }
  return request.requester_email ?? `User ${request.user_id}`;
}

function quotedResource(item: { facts?: ActivityItemFacts; title: string }): string {
  return item.facts?.subject ?? '';
}

function toHomeFeedItem(item: RecentActivityItem): HomeFeedItem {
  const copy = formatActivityCopy(item);
  return {
    kind: item.kind,
    title: copy.title,
    subtitleParts: copy.subtitleParts,
    timestamp: item.timestamp,
    relevant_to_user: item.relevant_to_user,
    target_ids: item.target_ids,
    facts: item.facts
  };
}

function withExtractionActionCount(item: HomeFeedItem, extraction: ActionExtractionResponse | undefined): HomeFeedItem {
  const count = extraction?.extracted_actions?.length;
  if (count == null || count === 0) return item;
  const facts: ActivityItemFacts = {
    actor: item.facts?.actor ?? null,
    subject: item.facts?.subject ?? '',
    metadata: { ...item.facts?.metadata, action_count: count }
  };
  const copy = formatActivityCopy({
    kind: item.kind as RecentActivityItem['kind'],
    facts,
    timestamp: item.timestamp,
    relevant_to_user: item.relevant_to_user,
    target_ids: item.target_ids
  });
  return { ...item, facts, title: copy.title, subtitleParts: copy.subtitleParts };
}

export function mergeHomeTimelineItems(
  items: RecentActivityItem[],
  extractions: ActionExtractionResponse[],
  kinds?: Set<ActivityItemKind>
): HomeFeedItem[] {
  const source = kinds ? items.filter((item) => kinds.has(item.kind)) : items;
  const allowed = source.map(toHomeFeedItem);
  const extractionById = new Map(extractions.map((extraction) => [extraction.id, extraction]));
  const reports = allowed
    .filter((item) => item.kind === 'extraction_report')
    .map((item) => withExtractionActionCount(item, extractionById.get(item.target_ids[0] ?? '')));

  const finalised = allowed.filter((item) => item.kind === 'meeting_finalised');
  const meetingExtractions = allowed.filter((item) => item.kind === 'extraction_meeting');
  const usedExtractionIndexes = new Set<number>();

  const mergedFinalised = finalised.map((item) => {
    const meetingName = quotedResource(item).toLowerCase();
    const pairIndex = meetingExtractions.findIndex((extractionItem, index) => {
      if (usedExtractionIndexes.has(index)) return false;
      const extractionName = quotedResource(extractionItem).toLowerCase();
      return Boolean(
        meetingName &&
          extractionName &&
          (extractionName === meetingName ||
            extractionName.includes(meetingName) ||
            meetingName.includes(extractionName))
      );
    });
    if (pairIndex < 0) return item;
    usedExtractionIndexes.add(pairIndex);
    const pair = meetingExtractions[pairIndex];
    const extraction = extractionById.get(pair.target_ids[0] ?? '');
    return withExtractionActionCount(
      {
        ...item,
        relevant_to_user: item.relevant_to_user || pair.relevant_to_user
      },
      extraction
    );
  });

  const leftoverMeetingExtractions = meetingExtractions
    .filter((_, index) => !usedExtractionIndexes.has(index))
    .map((item) => withExtractionActionCount(item, extractionById.get(item.target_ids[0] ?? '')));
  const other = allowed.filter(
    (item) =>
      item.kind !== 'extraction_report' && item.kind !== 'meeting_finalised' && item.kind !== 'extraction_meeting'
  );
  return collapseMemberAddedItems([...reports, ...mergedFinalised, ...leftoverMeetingExtractions, ...other]);
}

const MEMBER_ADD_BATCH_MS = 10 * 60 * 1000;

function activityTimeMs(value: string | null): number {
  return value ? parseActivityDate(value).getTime() : 0;
}

function memberAddedDisplayNames(item: HomeFeedItem): string[] {
  if (Array.isArray(item.facts?.metadata?.subjects) && item.facts.metadata.subjects.length) {
    return item.facts.metadata.subjects.filter((entry): entry is string => typeof entry === 'string');
  }
  if (item.facts?.subject) return [item.facts.subject];
  return item.subtitleParts;
}

function mergeMemberAddedItems(a: HomeFeedItem, b: HomeFeedItem): HomeFeedItem {
  const names = [...memberAddedDisplayNames(a), ...memberAddedDisplayNames(b)];
  const uniqueNames = [...new Set(names)];
  const aTime = activityTimeMs(a.timestamp);
  const bTime = activityTimeMs(b.timestamp);
  const mergedFacts = {
    actor: null,
    subject: uniqueNames[0] ?? 'members',
    metadata: {
      member_count: uniqueNames.length,
      subjects: uniqueNames
    }
  };
  const copy = formatActivityCopy({
    kind: 'member_added',
    facts: mergedFacts,
    timestamp: aTime >= bTime ? a.timestamp : b.timestamp,
    relevant_to_user: a.relevant_to_user || b.relevant_to_user,
    target_ids: [...a.target_ids, ...b.target_ids]
  });
  return {
    kind: 'member_added',
    title: copy.title,
    subtitleParts: copy.subtitleParts,
    timestamp: aTime >= bTime ? a.timestamp : b.timestamp,
    relevant_to_user: a.relevant_to_user || b.relevant_to_user,
    target_ids: [...a.target_ids, ...b.target_ids],
    facts: mergedFacts
  };
}

function collapseMemberAddedItems(items: HomeFeedItem[]): HomeFeedItem[] {
  const members = items
    .filter((item) => item.kind === 'member_added')
    .sort((a, b) => activityTimeMs(a.timestamp) - activityTimeMs(b.timestamp));
  const rest = items.filter((item) => item.kind !== 'member_added');
  const clustered: HomeFeedItem[] = [];
  for (const item of members) {
    const previous = clustered[clustered.length - 1];
    if (
      previous &&
      Math.abs(activityTimeMs(item.timestamp) - activityTimeMs(previous.timestamp)) <= MEMBER_ADD_BATCH_MS
    ) {
      clustered[clustered.length - 1] = mergeMemberAddedItems(previous, item);
    } else {
      clustered.push(item);
    }
  }
  return [...rest, ...clustered].sort((a, b) => activityTimeMs(b.timestamp) - activityTimeMs(a.timestamp));
}

export function joinRequestsToFeedItems(requests: JoinRequest[]): HomeFeedItem[] {
  return requests.map((request) => ({
    kind: 'join_requested' as const,
    title: `${requesterName(request)} requested to join`,
    subtitleParts: [request.message?.trim() || 'Requesting access'],
    timestamp: request.requested_at,
    relevant_to_user: true,
    target_ids: [request.id],
    joinRequest: request
  }));
}

function expiryTitle(name: string, subject: string, daysToExpiry: number): string {
  if (daysToExpiry < 0) {
    const n = Math.abs(daysToExpiry);
    return `${name}'s ${subject} expired ${n} ${n === 1 ? 'day' : 'days'} ago`;
  }
  if (daysToExpiry === 0) {
    return `${name}'s ${subject} expires today`;
  }
  return `${name}'s ${subject} expires in ${daysToExpiry} ${daysToExpiry === 1 ? 'day' : 'days'}`;
}

function trainingAlertSubtitle(appointment: AppointmentResponse): string[] {
  const subtitleParts = [appointment.role.name, getEfmDomainLabel(appointment.domain)];
  if (appointment.all_sites) {
    subtitleParts.push('All sites');
  } else if (appointment.sites.length > 0) {
    subtitleParts.push(appointment.sites.map((site) => site.name).join(', '));
  }
  if (appointment.record_expires_on) subtitleParts.push(`Expires ${formatShortDate(appointment.record_expires_on)}`);
  return subtitleParts;
}

/** One feed item per Training Record that is Expired or Expiring, worst first then soonest. */
export function trainingExpiryFeedItems(appointments: AppointmentResponse[]): HomeFeedItem[] {
  return listRecordAlerts(appointments).map(({ appointment, daysToExpiry }) => {
    const name = appointment.appointee.display_name;
    const subject = `${appointment.role.name} training record`;
    return {
      kind: 'appointment_expiring' as const,
      title: expiryTitle(name, subject, daysToExpiry),
      subtitleParts: trainingAlertSubtitle(appointment),
      timestamp: null,
      relevant_to_user: true,
      target_ids: [appointment.id],
      appointment
    };
  });
}

export function groupActivityByDay(items: HomeFeedItem[]): RecentActivityDayGroup[] {
  const now = new Date();
  const groups: RecentActivityDayGroup[] = [];
  const byKey = new Map<string, RecentActivityDayGroup>();
  for (const item of items) {
    const { key, label } = activityDayInfo(item.timestamp, now);
    let group = byKey.get(key);
    if (!group) {
      group = { key, label, items: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
}

function ActivitySubtitle({ parts }: { parts: string[] }) {
  if (parts.length === 0) return null;
  return (
    <p className="text-xs text-content-subtle flex items-center flex-wrap">
      {parts.map((part, index) => (
        <span key={`${part}-${index}`} className="inline-flex items-center">
          {index > 0 && <DotIcon size={14} weight="light" className="text-content-subtlest" aria-hidden />}
          {part}
        </span>
      ))}
    </p>
  );
}

function activityRowIsNavigable(kind: HomeFeedKind): boolean {
  return activityRowPillLabel(kind) !== null;
}

function activityRowPillLabel(kind: HomeFeedKind): string | null {
  switch (kind) {
    case 'join_requested':
      return 'Manage';
    case 'appointment_expiring':
      return 'Renew';
    case 'statutory_document_expired':
    case 'statutory_document_expiring':
      return 'Review';
    case 'extraction_meeting':
    case 'extraction_report':
      return 'View actions';
    case 'meeting_added':
    case 'meeting_finalised':
    case 'meeting_resource_added':
    case 'meeting_approval':
    case 'meeting_needs_finalising':
      return 'View meeting';
    case 'meeting_series_added':
      return 'View calendar';
    case 'action_created':
    case 'action_state_changed':
    case 'action_progress_update':
      return 'View action';
    case 'member_added':
      return 'View members';
    case 'resource_shared':
      return 'View file';
    case 'appointment_created':
    case 'appointment_revoked':
      return 'View training';
    case 'dashboard_data_entered':
      return 'View dashboard';
    case 'group_created':
    case 'certificate_added':
      return null;
    default:
      return 'View';
  }
}

function ActivityRowPill({ label }: { label: string }) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-800"
      aria-hidden
    >
      {label}
      <ArrowRightIcon size={12} weight="bold" />
    </span>
  );
}

function activityRowPillAlwaysVisible(kind: HomeFeedKind): boolean {
  return (
    kind === 'join_requested' ||
    kind === 'appointment_expiring' ||
    kind === 'statutory_document_expired' ||
    kind === 'statutory_document_expiring'
  );
}

function ActivityRowIcon({
  item,
  getActivityIcon
}: {
  item: HomeFeedItem;
  getActivityIcon: (item: RecentActivityItem) => ReactNode;
}) {
  if (item.kind === 'join_requested') return <UserPlusIcon size={18} weight="light" />;
  if (item.kind === 'appointment_expiring') return <GraduationCapIcon size={18} weight="light" />;
  if (item.kind === 'statutory_document_expired' || item.kind === 'statutory_document_expiring') {
    return <StatutoryDocumentIcon />;
  }
  if (item.kind === 'meeting_needs_finalising') return <CalendarBlankIcon size={18} weight="light" />;
  return getActivityIcon(item as RecentActivityItem);
}

function ActivityRowBody({
  item,
  getActivityIcon,
  circledIcon,
  groupAsPill = false,
  showGroupName = false,
  hideSubtitle = false
}: {
  item: HomeFeedItem;
  getActivityIcon: (item: RecentActivityItem) => ReactNode;
  circledIcon: boolean;
  groupAsPill?: boolean;
  showGroupName?: boolean;
  hideSubtitle?: boolean;
}) {
  const pill = activityRowPillLabel(item.kind);
  return (
    <>
      {circledIcon ? (
        <span className="relative z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50 text-content-subtle">
          <ActivityRowIcon item={item} getActivityIcon={getActivityIcon} />
        </span>
      ) : (
        <div className={`text-content-subtle ${hideSubtitle ? '' : 'mt-1'}`}>
          <ActivityRowIcon item={item} getActivityIcon={getActivityIcon} />
        </div>
      )}
      <span className={`min-w-0 flex-1 ${hideSubtitle ? '' : 'pt-0.5'}`}>
        <span className="flex items-center gap-2 min-w-0">
          <p className="text-sm font-medium text-content-default truncate">{item.title}</p>
          {groupAsPill && !hideSubtitle && item.groupName ? (
            <Pill size="sm" className="shrink-0 bg-gray-100 text-gray-600">
              {item.groupName}
            </Pill>
          ) : null}
          {showGroupName && !groupAsPill && item.groupName ? (
            <span className="truncate text-xs text-content-subtle">{item.groupName}</span>
          ) : null}
        </span>
        {hideSubtitle ? (
          item.groupName ? (
            <p className="text-xs text-content-subtle truncate">{item.groupName}</p>
          ) : null
        ) : (
          <ActivitySubtitle parts={item.subtitleParts} />
        )}
      </span>
      <span className={`flex shrink-0 items-center gap-2 ${hideSubtitle ? '' : 'self-start pt-0.5'}`}>
        {pill && activityRowPillAlwaysVisible(item.kind) ? <ActivityRowPill label={pill} /> : null}
        <span className="text-xs text-content-subtle whitespace-nowrap">{formatActivityTime(item.timestamp)}</span>
      </span>
    </>
  );
}

function ActivityRow({
  item,
  getActivityIcon,
  onItemClick,
  circledIcon,
  groupAsPill = false,
  showGroupName = false,
  hideSubtitle = false
}: {
  item: HomeFeedItem;
  getActivityIcon: (item: RecentActivityItem) => ReactNode;
  onItemClick: (item: HomeFeedItem) => void;
  circledIcon: boolean;
  groupAsPill?: boolean;
  showGroupName?: boolean;
  hideSubtitle?: boolean;
}) {
  const navigable = activityRowIsNavigable(item.kind);
  const body = (
    <ActivityRowBody
      item={item}
      getActivityIcon={getActivityIcon}
      circledIcon={circledIcon}
      groupAsPill={groupAsPill}
      showGroupName={showGroupName}
      hideSubtitle={hideSubtitle}
    />
  );
  if (!navigable) {
    return <div className={`flex w-full gap-3 text-left ${hideSubtitle ? 'items-center' : 'items-start'}`}>{body}</div>;
  }
  const button = (
    <button
      type="button"
      className={`flex w-full cursor-pointer gap-3 text-left ${hideSubtitle ? 'items-center' : 'items-start'}`}
      onClick={() => onItemClick(item)}
    >
      {body}
    </button>
  );
  const tooltip = activityRowPillLabel(item.kind);
  if (tooltip && !activityRowPillAlwaysVisible(item.kind)) {
    return <Tooltip title={tooltip}>{button}</Tooltip>;
  }
  return button;
}

export function RequiresActionCardList({
  items,
  getActivityIcon,
  onItemClick,
  groupAsPill = false,
  showGroupName = false,
  hideSubtitle = false
}: {
  items: HomeFeedItem[];
  getActivityIcon: (item: RecentActivityItem) => ReactNode;
  onItemClick: (item: HomeFeedItem) => void;
  groupAsPill?: boolean;
  showGroupName?: boolean;
  hideSubtitle?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => (
        <div
          key={item.joinRequest?.id ?? `${item.kind}-${item.target_ids[0] ?? index}`}
          className="rounded-xl border border-border-subtle px-3 py-2.5"
        >
          <ActivityRow
            item={item}
            getActivityIcon={getActivityIcon}
            onItemClick={onItemClick}
            circledIcon={false}
            groupAsPill={groupAsPill}
            showGroupName={showGroupName}
            hideSubtitle={hideSubtitle}
          />
        </div>
      ))}
    </div>
  );
}

export function ActivityTimeline({
  groups,
  onItemClick,
  getActivityIcon
}: {
  groups: RecentActivityDayGroup[];
  onItemClick: (item: HomeFeedItem) => void;
  getActivityIcon: (item: RecentActivityItem) => ReactNode;
}) {
  return (
    <div className="flex flex-col">
      {groups.map((group, groupIndex) => (
        <div key={group.key} className={groupIndex > 0 ? 'relative pt-4' : undefined}>
          {groupIndex > 0 && <div className="absolute left-4 top-0 bottom-0 w-px bg-border-subtle" aria-hidden />}
          <h4 className="relative z-[1] pl-11 pb-1 text-xs font-semibold uppercase tracking-wide text-content-default">
            {group.label}
          </h4>
          <div className="flex flex-col">
            {group.items.map((item, index) => {
              const isFirst = groupIndex === 0 && index === 0;
              const isLast = groupIndex === groups.length - 1 && index === group.items.length - 1;
              return (
                <div key={`${group.key}-${index}`} className="relative w-full text-left py-2.5 rounded-lg">
                  {!isFirst && <div className="absolute left-4 top-0 h-[1.625rem] w-px bg-border-subtle" aria-hidden />}
                  {!isLast && (
                    <div className="absolute left-4 top-[1.625rem] bottom-0 w-px bg-border-subtle" aria-hidden />
                  )}
                  <ActivityRow item={item} getActivityIcon={getActivityIcon} onItemClick={onItemClick} circledIcon />
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const formatMeetingWhen = (meeting: Meeting) => {
  const date = new Date(meeting.scheduled_date);
  const datePart = Number.isNaN(date.getTime())
    ? meeting.scheduled_date
    : date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = meeting.scheduled_time?.slice(0, 5);
  return time ? `${datePart} - ${time}` : datePart;
};

const daysUntilMeeting = (scheduledDate: string) => {
  const meetingDate = new Date(`${scheduledDate}T00:00:00`);
  if (Number.isNaN(meetingDate.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((meetingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

function ProgressStatusCard({
  title,
  subtitle,
  percent,
  description,
  barClassName,
  barColor,
  barSegments,
  onOpen
}: {
  title: string;
  subtitle: string;
  percent: number;
  description?: ReactNode;
  barClassName?: string;
  barColor?: string;
  barSegments?: { label: string; value: number; color: string }[];
  onOpen?: () => void;
}) {
  const clamped = Math.max(0, Math.min(100, percent));
  const fillClass = barColor ? '' : (barClassName ?? (clamped >= 70 ? 'bg-emerald-500' : 'bg-orange-400'));
  const visibleSegments = (barSegments ?? []).filter((segment) => segment.value > 0);
  const segmentTotal = visibleSegments.reduce((sum, segment) => sum + segment.value, 0);
  return (
    <TitledPanel
      size="sm"
      title={title}
      subtitle={subtitle}
      actions={
        onOpen ? (
          <Button variant="ghost" size="sm" icon={ArrowSquareOutIcon} aria-label={`Open ${title}`} onClick={onOpen} />
        ) : undefined
      }
    >
      <div>
        <div className="mt-1 flex items-baseline gap-2">
          <div className="text-2xl font-bold text-content-default lining-nums">{percent}%</div>
          {description}
        </div>
        <div className="mt-1 h-3 w-full rounded-full bg-gray-100 overflow-hidden flex">
          {visibleSegments.length > 0 && segmentTotal > 0 ? (
            visibleSegments.map((segment, index) => (
              <div
                key={segment.label}
                className={`h-full ${index === 0 ? 'rounded-l-full' : ''} ${
                  index === visibleSegments.length - 1 ? 'rounded-r-full' : ''
                }`}
                style={{
                  width: `${(segment.value / segmentTotal) * 100}%`,
                  backgroundColor: segment.color
                }}
                title={`${segment.label} · ${segment.value}`}
              />
            ))
          ) : (
            <div
              className={`h-full rounded-full ${fillClass}`}
              style={{ width: `${clamped}%`, ...(barColor ? { backgroundColor: barColor } : {}) }}
            />
          )}
        </div>
      </div>
    </TitledPanel>
  );
}

function GroupOverviewSkeleton() {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading overview">
      <div className="flex flex-col gap-1">
        <Skeleton className="h-5 w-[92%]" />
        <Skeleton className="h-5 w-[72%]" />
        <Skeleton className="h-5 w-[78%]" />
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Skeleton className="h-7 w-[72px] rounded-md" />
        <Skeleton className="h-7 w-[168px] rounded-md" />
        <Skeleton className="h-7 w-[156px] rounded-md" />
      </div>
    </div>
  );
}

function GroupOverviewPanel({
  groupId,
  onFollowUp
}: {
  groupId: string | undefined;
  onFollowUp: (prompt: string) => void;
}) {
  const overview = useGroupOverview(groupId);

  return (
    <TitledPanel
      size="md"
      title={
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-content-default">Your Overview</h3>
          <span className="inline-flex items-center gap-1 rounded-full bg-primary-lightblue px-2.5 py-0.5 text-xs font-medium text-secondary-blue">
            <SparkleIcon size={12} weight="fill" />
            AI summary
          </span>
        </div>
      }
      actions={
        groupId ? (
          <div className="flex items-center text-content-subtlest">
            {overview.data?.generated_at ? (
              <>
                <p className="text-xs text-content-subtlest whitespace-nowrap pr-1">
                  Generated{' '}
                  {new Date(overview.data.generated_at).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </p>
                <DotIcon size={14} weight="light" aria-hidden />
              </>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              text="Refresh"
              className="text-content-subtlest"
              icon={ArrowsClockwiseIcon}
              loading={overview.refresh.isPending}
              disabled={overview.isFetching || overview.refresh.isPending}
              onClick={() => overview.refresh.mutate()}
            />
          </div>
        ) : null
      }
    >
      {!groupId ? (
        <p className="text-sm text-content-subtle">No overview data for this week.</p>
      ) : overview.isPending || (overview.isFetching && !overview.data) ? (
        <GroupOverviewSkeleton />
      ) : overview.isError || overview.refresh.isError ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-misc-error">Could not generate the group overview.</p>
          <Button variant="outline" size="sm" text="Try again" onClick={() => overview.refresh.mutate()} />
        </div>
      ) : (
        <div className="space-y-2">
          <GroupsOverviewMarkdown
            body={overview.data?.body ?? 'No overview data for this week.'}
            resourceLinks={overview.data?.resource_links}
            groupId={groupId}
          />
          <div className="flex min-w-0 items-center gap-2 pt-1">
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
              {(overview.data?.follow_ups ?? []).map((followUp) => (
                <Button
                  key={followUp.label}
                  variant="outline"
                  size="sm"
                  text={followUp.label}
                  icon={ChatIcon}
                  className={OVERVIEW_FOLLOW_UP_BUTTON_CLASS}
                  title={followUp.label}
                  disabled={!groupId}
                  onClick={() => onFollowUp(followUp.prompt)}
                />
              ))}
            </div>
            <GroupsOverviewAskInput prefix={GROUP_OVERVIEW_QUESTION_PREFIX} onSend={onFollowUp} disabled={!groupId} />
          </div>
        </div>
      )}
    </TitledPanel>
  );
}

export default function GroupHomeTab({
  setupCard,
  upcomingMeetings,
  actions,
  currentUserId,
  recentActivity,
  isLoadingActivity,
  onActivityItemClick,
  getActivityIcon,
  onEditAction,
  onViewAllActions,
  onViewAllMeetings,
  onOpenMeeting,
  meetingsNeedingFinalising = [],
  onCreateMeetingSeries,
  training,
  onOpenTraining,
  pam,
  onOpenPam,
  statutoryCompliance,
  onOpenStatutoryCompliance,
  onOpenMembers,
  groupId,
  groupName,
  pendingJoinRequests = [],
  trainingAppointments = [],
  onOpenTrainingAppointment,
  statutoryDocuments = [],
  extractions = [],
  canViewRequiresAction = false,
  reportSet = 'default'
}: GroupHomeTabProps) {
  const location = useLocation();
  const tenantCode = resolveTenantCode(location.pathname);
  const statutoryExpiryItems = useMemo(
    () => statutoryDocumentExpiryFeedItems(statutoryDocuments),
    [statutoryDocuments]
  );

  const [activityView, setActivityView] = useState<ActivityView>(() =>
    canViewRequiresAction &&
    (pendingJoinRequests.length > 0 ||
      statutoryExpiryItems.length > 0 ||
      listRecordAlerts(trainingAppointments).length > 0)
      ? 'requires_action'
      : 'timeline'
  );
  const [userChoseActivityView, setUserChoseActivityView] = useState(false);
  const [isAllActivityOpen, setIsAllActivityOpen] = useState(false);
  const [activityTypeFilter, setActivityTypeFilter] = useState<ActivityTypeFilter>('all');

  const requiresActionItems = useMemo(
    () =>
      canViewRequiresAction
        ? [
            ...meetingsNeedingFinalising.map((meeting) => ({
              ...unfinalisedMeetingToFeedItem(meeting),
              meeting
            })),
            ...statutoryExpiryItems,
            ...trainingExpiryFeedItems(trainingAppointments),
            ...joinRequestsToFeedItems(pendingJoinRequests)
          ]
        : [],
    [canViewRequiresAction, meetingsNeedingFinalising, statutoryExpiryItems, trainingAppointments, pendingJoinRequests]
  );

  useEffect(() => {
    if (userChoseActivityView) return;
    setActivityView(requiresActionItems.length > 0 ? 'requires_action' : 'timeline');
  }, [requiresActionItems.length, userChoseActivityView]);

  const homeActivity = useMemo(
    () =>
      mergeHomeTimelineItems(recentActivity, extractions, HOME_TIMELINE_KINDS).filter(
        (item) => item.kind !== 'action_created'
      ),
    [recentActivity, extractions]
  );

  const activityGroups = useMemo(
    () => groupActivityByDay(homeActivity.slice(0, ACTIVITY_PREVIEW_COUNT)),
    [homeActivity]
  );

  const allActivityItems = useMemo(
    () => mergeHomeTimelineItems(recentActivity, extractions),
    [recentActivity, extractions]
  );

  const allActivityGroups = useMemo(() => {
    const items =
      activityTypeFilter === 'all'
        ? allActivityItems
        : allActivityItems.filter((item) => ACTIVITY_TYPE_KINDS[activityTypeFilter].has(item.kind));
    return groupActivityByDay(items);
  }, [allActivityItems, activityTypeFilter]);

  const homeRequiresActionCount = requiresActionItems.length;

  const myDueActions = useMemo(() => {
    const open = actions.filter((action) => action.status !== 'resolved' && action.status !== 'archived');
    const mine = currentUserId
      ? open.filter((action) => action.assignees?.some((assignee) => assignee.user_id === currentUserId))
      : open;
    return [...mine].sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });
  }, [actions, currentUserId]);

  const handleHomeItemClick = (item: HomeFeedItem) => {
    if (item.kind === 'join_requested') {
      onOpenMembers?.();
      return;
    }
    if (item.kind === 'appointment_expiring' && item.appointment) {
      onOpenTrainingAppointment?.(item.appointment);
      return;
    }
    if (item.kind === 'statutory_document_expired' || item.kind === 'statutory_document_expiring') {
      onOpenStatutoryCompliance?.();
      return;
    }
    if (item.kind === 'meeting_needs_finalising' && item.meeting) {
      onOpenMeeting(item.meeting.id);
      return;
    }
    onActivityItemClick(item as RecentActivityItem);
  };

  const meetingsPreview = upcomingMeetings.slice(0, MEETINGS_PREVIEW_COUNT);

  const openGroupReportChat = useCallback(
    (query: string) => {
      if (!groupId) return;
      const params = new URLSearchParams({
        q: query,
        resource_id: groupId,
        resource_type: 'team_group',
        resource_title: groupName || groupId
      });
      window.open(toTenantPath(`/chat?${params.toString()}`, tenantCode), '_blank');
    },
    [groupId, groupName, tenantCode]
  );

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <GroupOverviewPanel groupId={groupId} onFollowUp={openGroupReportChat} />

          <TitledPanel
            size="md"
            className="group-hub-recent-activity"
            title={
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-content-default">Activity Highlights</h3>
                <span className="text-xs text-content-subtle bg-gray-100 rounded-full px-2 py-0.5">
                  {homeActivity.length} {homeActivity.length === 1 ? 'item' : 'items'}
                </span>
              </div>
            }
            actions={
              <>
                <Button variant="outline" text="Show all activity" onClick={() => setIsAllActivityOpen(true)} />
                {canViewRequiresAction && (
                  <SegmentedControl
                    ariaLabel="Activity view"
                    value={activityView}
                    onChange={(next) => {
                      setUserChoseActivityView(true);
                      setActivityView(next);
                    }}
                    options={[
                      { value: 'timeline', label: 'Timeline', icon: ClockIcon },
                      {
                        value: 'requires_action',
                        label: (
                          <span className="inline-flex items-center gap-1.5">
                            Requires Action
                            {homeRequiresActionCount > 0 && <CountBadge count={homeRequiresActionCount} />}
                          </span>
                        )
                      }
                    ]}
                  />
                )}
              </>
            }
          >
            {isLoadingActivity && homeActivity.length === 0 && requiresActionItems.length === 0 && (
              <Loading text="Loading activity..." />
            )}

            {!isLoadingActivity && activityView === 'timeline' && homeActivity.length === 0 && (
              <div className="text-center py-10 text-content-subtle border border-dashed border-border-subtle rounded-xl">
                <FileIcon size={40} weight="light" className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">No activity highlights in this group yet.</p>
              </div>
            )}

            {!isLoadingActivity && activityView === 'requires_action' && requiresActionItems.length === 0 && (
              <div className="text-center py-10 text-content-subtle border border-dashed border-border-subtle rounded-xl">
                <FileIcon size={40} weight="light" className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">Nothing needs your action right now.</p>
              </div>
            )}

            {activityGroups.length > 0 && activityView === 'timeline' && (
              <ActivityTimeline
                groups={activityGroups}
                onItemClick={handleHomeItemClick}
                getActivityIcon={getActivityIcon}
              />
            )}

            {activityView === 'requires_action' && requiresActionItems.length > 0 && (
              <RequiresActionCardList
                items={requiresActionItems}
                getActivityIcon={getActivityIcon}
                onItemClick={handleHomeItemClick}
              />
            )}
          </TitledPanel>

          <TitledPanel
            size="md"
            title="Your actions due"
            subtitle="Assigned to you — soonest due first"
            actions={
              <Button
                variant="outline"
                size="sm"
                text="View all"
                icon={ArrowRightIcon}
                iconPosition="right"
                onClick={onViewAllActions}
              />
            }
          >
            <ActionsDataTable
              data={myDueActions}
              onRowClick={onEditAction}
              emptyMessage="No open actions assigned to you."
              itemsPerPage={ACTIONS_PREVIEW_COUNT}
              showAssignees={false}
            />
          </TitledPanel>
        </div>

        <div className="group-hub-activity-sidebar flex flex-col gap-4">
          {setupCard}

          <HelpLinkList links={groupHubHelpLinks} className="max-lg:order-last" />

          <TitledPanel
            size="sm"
            title="Meetings"
            actions={
              <Button
                variant="outline"
                size="sm"
                text="View all"
                icon={ArrowRightIcon}
                iconPosition="right"
                onClick={onViewAllMeetings}
              />
            }
          >
            {meetingsPreview.length === 0 ? (
              <div className="text-center py-6 text-content-subtle border border-dashed border-border-subtle rounded-xl">
                <CalendarBlankIcon size={28} weight="light" className="mx-auto mb-2 opacity-40" />
                <p className="text-xs">No scheduled meetings</p>
                {onCreateMeetingSeries && (
                  <Button
                    variant="primary"
                    size="sm"
                    icon={CalendarBlankIcon}
                    text="New meeting series"
                    className="mt-3"
                    onClick={onCreateMeetingSeries}
                  />
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {meetingsPreview.map((meeting) => {
                  const daysUntil = daysUntilMeeting(meeting.scheduled_date);
                  return (
                    <button
                      key={meeting.id}
                      type="button"
                      onClick={() => onOpenMeeting(meeting.id)}
                      className="flex items-start gap-3 w-full text-left px-3 py-2.5 rounded-xl border border-border-subtle bg-background-default hover:bg-surface-hover"
                    >
                      <CalendarBlankIcon size={18} className="text-blue-500 shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-content-default truncate">
                          {meeting.title || 'Meeting'}
                        </p>
                        <p className="text-xs text-content-subtle">
                          {formatMeetingWhen(meeting)}
                          {daysUntil !== null && ` · ${daysUntil === 0 ? 'Today' : `${daysUntil} days until`}`}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </TitledPanel>

          {training && (
            <ProgressStatusCard
              title="Training"
              subtitle="Statutory role completion"
              percent={training.percent}
              description={<span className="text-xs text-content-subtle">{training.detail}</span>}
              onOpen={onOpenTraining}
            />
          )}

          {statutoryCompliance && (
            <ProgressStatusCard
              title="Statutory Evidence"
              subtitle="Required documents evidenced"
              percent={statutoryCompliance.percent}
              description={
                <span className="text-xs text-content-subtle flex items-center flex-wrap">
                  {statutoryCompliance.evidencedCount ?? 0} Evidenced
                  {(statutoryCompliance.expiringCount ?? 0) > 0 && (
                    <>
                      <DotIcon size={14} weight="light" className="text-content-subtlest" aria-hidden />
                      {statutoryCompliance.expiringCount} Expiring
                    </>
                  )}
                  {(statutoryCompliance.overdueCount ?? 0) > 0 && (
                    <>
                      <DotIcon size={14} weight="light" className="text-content-subtlest" aria-hidden />
                      {statutoryCompliance.overdueCount} Expired
                    </>
                  )}
                  {(statutoryCompliance.missingCount ?? 0) > 0 && (
                    <>
                      <DotIcon size={14} weight="light" className="text-content-subtlest" aria-hidden />
                      {statutoryCompliance.missingCount} Missing
                    </>
                  )}
                </span>
              }
              barSegments={statutoryCompliance.barSegments}
              onOpen={onOpenStatutoryCompliance}
            />
          )}

          {pam && (
            <ProgressStatusCard
              title="PAM"
              subtitle="Self-assessment health"
              percent={pam.percent}
              description={
                <span className="text-xs text-content-subtle flex items-center">
                  {pam.yesCount ?? 0} Yes
                  <DotIcon size={14} weight="light" className="text-content-subtlest" aria-hidden />
                  {pam.noCount ?? 0} No
                </span>
              }
              barClassName="bg-emerald-500"
              onOpen={onOpenPam}
            />
          )}

          <TitledPanel size="sm" title="Reports" subtitle="Let CompliMind draft your governance admin.">
            <div className="flex flex-col gap-2">
              {reportSet === 'pfi' ? (
                PFI_REPORTS.map((report) => (
                  <Button
                    key={report.label}
                    variant="primary"
                    icon={SparkleIcon}
                    text={report.label}
                    fullWidth
                    onClick={() => openGroupReportChat(report.query)}
                    disabled={!groupId}
                  />
                ))
              ) : (
                <>
                  <Button
                    variant="primary"
                    icon={SparkleIcon}
                    text="Generate Chair's report"
                    fullWidth
                    onClick={() => openGroupReportChat(CHAIR_REPORT_CHAT_QUERY)}
                    disabled={!groupId}
                  />
                  <Button
                    variant="primary"
                    icon={SparkleIcon}
                    text="Generate operational report"
                    fullWidth
                    onClick={() => openGroupReportChat(OPERATIONAL_REPORT_CHAT_QUERY)}
                    disabled={!groupId}
                  />
                </>
              )}
            </div>
          </TitledPanel>
        </div>
      </div>

      <Modal
        open={isAllActivityOpen}
        onClose={() => {
          setIsAllActivityOpen(false);
          setActivityTypeFilter('all');
        }}
        title="Recent Activity"
        className="w-full max-w-2xl"
      >
        {recentActivity.length === 0 ? (
          <p className="text-sm text-content-subtle">No recent activity in this group yet.</p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex justify-end">
              <Dropdown
                size="sm"
                width="w-44"
                ariaLabel="Filter activity by type"
                prefix="Type"
                options={[...ACTIVITY_TYPE_OPTIONS]}
                initialValue={activityTypeFilter}
                onChange={(value) => setActivityTypeFilter(String(value) as ActivityTypeFilter)}
              />
            </div>
            {allActivityGroups.length === 0 ? (
              <p className="text-sm text-content-subtle">No activity of this type.</p>
            ) : (
              <ActivityTimeline
                groups={allActivityGroups}
                onItemClick={(item) => {
                  handleHomeItemClick(item);
                  setIsAllActivityOpen(false);
                  setActivityTypeFilter('all');
                }}
                getActivityIcon={getActivityIcon}
              />
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
